import React, { useState, useEffect, useMemo } from 'react';
import {
  History, Search, X, Filter, ArrowUpDown, Loader2,
  AlertCircle, ChevronLeft, ChevronRight,
  TrendingUp, TrendingDown, Plus, Minus,
  Package, Calendar, User, FileText, BarChart2
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';

/* ──────────────────────────────────────────────
   CONSTANTS
   ────────────────────────────────────────────── */
const PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

/* ──────────────────────────────────────────────
   HELPERS
   ────────────────────────────────────────────── */
const formatDate = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateShort = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/* ──────────────────────────────────────────────
   TYPE BADGE
   ────────────────────────────────────────────── */
const TypeBadge = ({ type }) => {
  const t = (type || '').toLowerCase();
  if (t === 'increase')
    return (
      <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-emerald-200">
        <Plus className="w-3 h-3" /> Increase
      </span>
    );
  if (t === 'decrease')
    return (
      <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-red-200">
        <Minus className="w-3 h-3" /> Decrease
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-slate-200">
      <BarChart2 className="w-3 h-3" /> {type || 'Other'}
    </span>
  );
};

/* ──────────────────────────────────────────────
   FLOW ARROW  (Before → Adj → After)
   ────────────────────────────────────────────── */
const FlowArrow = ({ before, adjQty, after, type }) => {
  const isIncrease = (type || '').toLowerCase() === 'increase';
  const isDecrease = (type || '').toLowerCase() === 'decrease';
  return (
    <div className="flex items-center justify-center gap-1.5">
      <span className="text-sm font-bold text-gray-700">{before}</span>
      <span className="text-gray-300 text-xs">→</span>
      <span className={`text-xs font-bold px-1.5 py-0.5 rounded border ${isIncrease ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
          isDecrease ? 'bg-red-50 text-red-700 border-red-200' :
            'bg-slate-100 text-slate-700 border-slate-200'
        }`}>
        {isIncrease ? '+' : isDecrease ? '−' : '±'}{Math.abs(adjQty)}
      </span>
      <span className="text-gray-300 text-xs">→</span>
      <span className="text-sm font-bold text-slate-700">{after}</span>
    </div>
  );
};

/* ══════════════════════════════════════════════
   MAIN
   ══════════════════════════════════════════════ */
export default function StockAdjustmentList() {
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [sortKey, setSortKey] = useState('adjustmentDate');
  const [sortDir, setSortDir] = useState('desc');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [deletingId, setDeletingId] = useState(null);
  const [selectedAdj, setSelectedAdj] = useState(null);

  useEffect(() => { fetchAdjustments(); }, []);

  const fetchAdjustments = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}api/StockAdjustment`);
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      setAdjustments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load adjustments');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let data = [...adjustments];

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      data = data.filter(
        (a) =>
          a.adjustmentNo?.toLowerCase().includes(q) ||
          a.itemCode?.toLowerCase().includes(q) ||
          a.itemDescription?.toLowerCase().includes(q) ||
          a.adjustedByName?.toLowerCase().includes(q)
      );
    }
    if (filterType)
      data = data.filter((a) => a.adjustmentType.toLowerCase() === filterType.toLowerCase());
    if (filterDateFrom) {
      const from = new Date(filterDateFrom);
      data = data.filter((a) => new Date(a.adjustmentDate) >= from);
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo);
      to.setHours(23, 59, 59);
      data = data.filter((a) => new Date(a.adjustmentDate) <= to);
    }

    data.sort((a, b) => {
      let valA = a[sortKey], valB = b[sortKey];
      if (sortKey === 'adjustmentDate') { valA = new Date(valA); valB = new Date(valB); }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [adjustments, searchText, filterType, filterDateFrom, filterDateTo, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize);

  useEffect(() => { setCurrentPage(1); }, [searchText, filterType, filterDateFrom, filterDateTo, pageSize]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };
  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3.5 h-3.5 opacity-30" />;
    return <span className="inline-flex text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const totalIncrease = adjustments
    .filter((a) => a.adjustmentType.toLowerCase() === 'increase')
    .reduce((s, a) => s + (Number(a.adjustmentQty) || 0), 0);
  const totalDecrease = adjustments
    .filter((a) => a.adjustmentType.toLowerCase() === 'decrease')
    .reduce((s, a) => s + (Number(a.adjustmentQty) || 0), 0);
  const netChange = totalIncrease - totalDecrease;

  const hasActiveFilters = searchText.trim() || filterType || filterDateFrom || filterDateTo;
  const clearFilters = () => {
    setSearchText(''); setFilterType(''); setFilterDateFrom(''); setFilterDateTo('');
  };

  const handleDelete = async (adj) => {
    const { value: confirmed } = await Swal.fire({
      icon: 'warning',
      title: 'Reverse Adjustment?',
      html: `Are you sure you want to reverse <strong>${adj.adjustmentNo}</strong>?<br><small class="text-gray-500">Stock will be restored to <strong>${adj.quantityBefore}</strong></small>`,
      showCancelButton: true,
      confirmButtonText: 'Yes, Reverse',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
    });
    if (!confirmed) return;

    setDeletingId(adj.id);
    try {
      const res = await fetch(`${API_BASE_URL}api/StockAdjustment/${adj.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        throw new Error(errorData?.message || `Delete failed (${res.status})`);
      }
      const user = JSON.parse(localStorage.getItem("user"));
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "DELETE",
          module: "Inventory",
          description: `Stock Adjustment reversed - Adj#: ${adj.adjustmentNo}, Item: ${adj.itemCode}, Stock restored to: ${adj.quantityBefore}`,
          userId: user?.userId,
          username: user?.username,
          fullName: user?.fullName || user?.username,
        })
      }).catch(err => console.error("Log failed:", err));
      Swal.fire({
        icon: 'success',
        title: 'Reversed!',
        html: `<strong>${adj.adjustmentNo}</strong> has been reversed.<br><small>Stock restored to ${adj.quantityBefore}</small>`,
        confirmButtonColor: '#10b981',
        timer: 2000,
        showConfirmButton: false,
      });
      fetchAdjustments();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: '#dc2626' });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">

        {/* ── HEADER ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <History className="w-6 h-6 text-slate-600" />
                Stock Adjustment History
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Total records: <span className="font-semibold text-slate-700">{adjustments.length}</span>
                {hasActiveFilters && (
                  <span className="ml-2 text-amber-600 font-medium">· Showing {filtered.length} filtered</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={fetchAdjustments} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition text-gray-600" title="Refresh">
                <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                onClick={() => setShowFilters((v) => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition ${showFilters || hasActiveFilters
                    ? 'border-slate-400 bg-slate-100 text-slate-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                  }`}
              >
                <Filter className="w-4 h-4" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-1 bg-slate-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {[searchText.trim(), filterType, filterDateFrom, filterDateTo].filter(Boolean).length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── SUMMARY CARDS ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          {[
            { label: 'Total Adjustments', value: adjustments.length, icon: History, color: 'slate', sub: 'all records' },
            { label: 'Stock Increased', value: totalIncrease, icon: TrendingUp, color: 'emerald', sub: 'total units added' },
            { label: 'Stock Decreased', value: totalDecrease, icon: TrendingDown, color: 'red', sub: 'total units removed' },
            { label: 'Net Change', value: netChange, icon: BarChart2, color: 'teal', sub: netChange >= 0 ? 'net positive' : 'net negative' },
          ].map((card, i) => {
            const Icon = card.icon;
            const colors = {
              slate: { bg: 'bg-slate-50', border: 'border-slate-200', icon: 'text-slate-600', val: 'text-slate-900' },
              emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-600', val: 'text-emerald-900' },
              red: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', val: 'text-red-900' },
              teal: { bg: 'bg-teal-50', border: 'border-teal-200', icon: 'text-teal-600', val: 'text-teal-900' },
            };
            const c = colors[card.color];
            return (
              <div key={i} className={`${c.bg} border ${c.border} rounded-xl p-4 shadow-sm`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-600 font-semibold uppercase tracking-wide">{card.label}</span>
                  <Icon className={`w-5 h-5 ${c.icon}`} />
                </div>
                <p className={`text-2xl font-bold ${c.val}`}>{card.value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{card.sub}</p>
              </div>
            );
          })}
        </div>

        {/* ── FILTER PANEL ── */}
        {showFilters && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search Adj No / Item…"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white text-gray-700"
              >
                <option value="">All Types</option>
                <option value="increase">Increase</option>
                <option value="decrease">Decrease</option>
                <option value="physical count">Physical Count</option>
              </select>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
              </div>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="mt-3 text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1">
                <X className="w-3 h-3" /> Clear all filters
              </button>
            )}
          </div>
        )}

        {/* ── TABLE ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">

          {loading && (
            <div className="flex flex-col items-center justify-center py-24">
              <Loader2 className="w-10 h-10 text-slate-500 animate-spin mb-3" />
              <p className="text-gray-500 text-sm">Loading adjustments…</p>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-24">
              <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
              <p className="text-red-600 font-semibold">{error}</p>
              <button onClick={fetchAdjustments} className="mt-2 text-sm text-slate-600 underline">Retry</button>
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24">
              <History className="w-14 h-14 text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">
                {hasActiveFilters ? 'No adjustments match your filters.' : 'No adjustments yet.'}
              </p>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="mt-2 text-sm text-slate-600 underline">Clear filters</button>
              )}
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider w-10">#</th>
                      {[
                        { key: 'adjustmentNo', label: 'Adj No' },
                        { key: 'adjustmentDate', label: 'Date' },
                        { key: 'itemCode', label: 'Item' },
                        { key: 'adjustmentType', label: 'Type' },
                      ].map((col) => (
                        <th
                          key={col.key}
                          onClick={() => handleSort(col.key)}
                          className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer select-none hover:text-slate-900 transition"
                        >
                          <span className="inline-flex items-center gap-1.5">{col.label}<SortIcon col={col.key} /></span>
                        </th>
                      ))}
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Stock Flow</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Reason</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">By</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginated.map((adj, idx) => {
                      const rowNum = (safeCurrentPage - 1) * pageSize + idx + 1;
                      const isDeleting = deletingId === adj.id;
                      return (
                        <tr
                          key={adj.id}
                          className={`border-b border-gray-100 transition-colors ${isDeleting ? 'bg-red-50' : 'hover:bg-slate-50'}`}
                        >
                          <td className="px-4 py-3 text-gray-400 font-medium">{rowNum}</td>
                          <td className="px-4 py-3">
                            <span className="font-mono font-semibold text-slate-800">{adj.adjustmentNo}</span>
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-xs flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            {formatDate(adj.adjustmentDate)}
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono font-semibold text-gray-800">{adj.itemCode}</span>
                            <p className="text-xs text-gray-500 truncate max-w-[140px]">{adj.itemDescription}</p>
                          </td>
                          <td className="px-4 py-3"><TypeBadge type={adj.adjustmentType} /></td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <FlowArrow
                              before={adj.quantityBefore}
                              adjQty={adj.adjustmentQty}
                              after={adj.quantityAfter}
                              type={adj.adjustmentType}
                            />
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-xs max-w-[120px] truncate">{adj.reason || '—'}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 text-gray-600 text-xs">
                              <User className="w-3 h-3 text-gray-400" />
                              {adj.adjustedByName || (adj.adjustedBy ? `User ${adj.adjustedBy}` : '—')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="inline-flex items-center gap-1.5">
                              <button
                                onClick={() => setSelectedAdj(adj)}
                                className="inline-flex items-center gap-1 bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg shadow-sm transition"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7s-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDelete(adj)}
                                disabled={isDeleting}
                                className="inline-flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                {isDeleting ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                  </svg>
                                )}
                                {isDeleting ? 'Reversing…' : 'Reverse'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ── PAGINATION ── */}
              <div className="flex flex-col sm:flex-row items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50 gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="border border-gray-300 rounded-lg px-2 py-1 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  >
                    {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <span className="text-gray-400">
                    ({(safeCurrentPage - 1) * pageSize + 1}–{Math.min(safeCurrentPage * pageSize, filtered.length)} of {filtered.length})
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button disabled={safeCurrentPage === 1} onClick={() => setCurrentPage(1)}
                    className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
                  </button>
                  <button disabled={safeCurrentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 transition">
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {(() => {
                    const pages = []; const range = 2;
                    let start = Math.max(1, safeCurrentPage - range);
                    let end = Math.min(totalPages, safeCurrentPage + range);
                    if (start > 1) { pages.push(1); if (start > 2) pages.push('...'); }
                    for (let i = start; i <= end; i++) pages.push(i);
                    if (end < totalPages) { if (end < totalPages - 1) pages.push('...'); pages.push(totalPages); }
                    return pages.map((p, i) =>
                      p === '...' ? (
                        <span key={`e${i}`} className="px-1 text-gray-400 text-sm">…</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setCurrentPage(p)}
                          className={`w-8 h-8 rounded-md text-sm font-semibold transition ${p === safeCurrentPage
                              ? 'bg-slate-700 text-white shadow-sm'
                              : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                          {p}
                        </button>
                      )
                    );
                  })()}

                  <button disabled={safeCurrentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 transition">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button disabled={safeCurrentPage === totalPages} onClick={() => setCurrentPage(totalPages)}
                    className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════
          DETAIL MODAL
          ═══════════════════════════════════════ */}
      {selectedAdj && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col">

            <div className="bg-gradient-to-r from-indigo-700 to-indigo-600 text-white px-6 py-4 flex-shrink-0 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <History className="w-5 h-5" />
                <div>
                  <h3 className="text-lg font-bold font-mono">{selectedAdj.adjustmentNo}</h3>
                  <p className="text-slate-300 text-xs">{formatDateShort(selectedAdj.adjustmentDate)}</p>
                </div>
              </div>
              <button onClick={() => setSelectedAdj(null)} className="bg-white/10 hover:bg-white/20 rounded-lg p-1.5 transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">

              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Before</p>
                  <p className="text-2xl font-bold text-gray-900">{selectedAdj.quantityBefore}</p>
                  <p className="text-xs text-gray-500">units</p>
                </div>
                <div className={`border rounded-lg p-3 text-center ${(selectedAdj.adjustmentType || '').toLowerCase() === 'increase'
                    ? 'bg-emerald-50 border-emerald-200'
                    : (selectedAdj.adjustmentType || '').toLowerCase() === 'decrease'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-slate-100 border-slate-200'
                  }`}>
                  <p className={`text-xs font-semibold uppercase mb-1 ${(selectedAdj.adjustmentType || '').toLowerCase() === 'increase' ? 'text-emerald-700' :
                      (selectedAdj.adjustmentType || '').toLowerCase() === 'decrease' ? 'text-red-700' : 'text-slate-700'
                    }`}>Adjustment</p>
                  <p className={`text-2xl font-bold ${(selectedAdj.adjustmentType || '').toLowerCase() === 'increase' ? 'text-emerald-900' :
                      (selectedAdj.adjustmentType || '').toLowerCase() === 'decrease' ? 'text-red-900' : 'text-slate-900'
                    }`}>
                    {(selectedAdj.adjustmentType || '').toLowerCase() === 'increase' ? '+' : '−'}{Math.abs(selectedAdj.adjustmentQty)}
                  </p>
                  <p className={`text-xs ${(selectedAdj.adjustmentType || '').toLowerCase() === 'increase' ? 'text-emerald-500' :
                      (selectedAdj.adjustmentType || '').toLowerCase() === 'decrease' ? 'text-red-500' : 'text-slate-500'
                    }`}>units</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-700 font-semibold uppercase mb-1">After</p>
                  <p className="text-2xl font-bold text-slate-900">{selectedAdj.quantityAfter}</p>
                  <p className="text-xs text-slate-500">units</p>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                <table className="w-full text-sm">
                  <tbody>
                    {[
                      { label: 'Item Code', value: <span className="font-mono font-semibold text-gray-800">{selectedAdj.itemCode}</span> },
                      { label: 'Description', value: <span className="text-gray-700">{selectedAdj.itemDescription || '—'}</span> },
                      {
                        label: 'Warehouse', value: selectedAdj.warehouseCode
                          ? <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md text-xs font-semibold border border-slate-200">
                            <Package className="w-3 h-3" />{selectedAdj.warehouseCode}
                          </span>
                          : <span className="text-gray-400">—</span>
                      },
                      {
                        label: 'Batch No', value: selectedAdj.batchNo
                          ? <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs font-semibold border border-slate-200">{selectedAdj.batchNo}</span>
                          : <span className="text-gray-400">—</span>
                      },
                      { label: 'Adjustment Type', value: <TypeBadge type={selectedAdj.adjustmentType} /> },
                      { label: 'Adjustment Date', value: <span className="text-gray-700 flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-gray-400" />{formatDate(selectedAdj.adjustmentDate)}</span> },
                      { label: 'Adjusted By', value: <span className="text-gray-700 flex items-center gap-1"><User className="w-3.5 h-3.5 text-gray-400" />{selectedAdj.adjustedByName || (selectedAdj.adjustedBy ? `User ${selectedAdj.adjustedBy}` : '—')}</span> },
                      { label: 'Reason', value: <span className="text-gray-700 italic">{selectedAdj.reason || '—'}</span> },
                      { label: 'Remarks', value: <span className="text-gray-600 text-xs">{selectedAdj.remarks || '—'}</span> },
                    ].map((row, i) => (
                      <tr key={i} className={`border-b border-gray-100 last:border-0 ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                        <td className="px-4 py-2.5 text-xs font-bold text-slate-700 uppercase tracking-wide w-36">{row.label}</td>
                        <td className="px-4 py-2.5">{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => { setSelectedAdj(null); handleDelete(selectedAdj); }}
                  className="inline-flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold px-4 py-2 rounded-lg shadow-sm transition"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  Reverse This Adjustment
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end px-6 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <button onClick={() => setSelectedAdj(null)} className="text-sm text-gray-500 hover:text-gray-700 font-medium transition">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}